import { useState, type MouseEvent, type ReactNode } from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import DeleteIcon from '@mui/icons-material/Delete';

interface ImageContextMenuProps {
  children: ReactNode;
  onRegenerate?: () => void;
  onEditPrompt?: () => void;
  onVariation?: () => void;
  onChangePosition?: () => void;
  onRemove?: () => void;
}

export function ImageContextMenu({
  children,
  onRegenerate,
  onEditPrompt,
  onVariation,
  onChangePosition,
  onRemove,
}: ImageContextMenuProps) {
  const [anchorPos, setAnchorPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    setAnchorPos({ top: e.clientY, left: e.clientX });
  };

  const close = () => setAnchorPos(null);

  const action = (fn?: () => void) => () => {
    close();
    fn?.();
  };

  return (
    <>
      <div onContextMenu={handleContextMenu}>{children}</div>
      <Menu
        open={anchorPos !== null}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={anchorPos ?? undefined}
      >
        {onRegenerate && (
          <MenuItem onClick={action(onRegenerate)}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Regenerate</ListItemText>
          </MenuItem>
        )}
        {onEditPrompt && (
          <MenuItem onClick={action(onEditPrompt)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit prompt</ListItemText>
          </MenuItem>
        )}
        {onVariation && (
          <MenuItem onClick={action(onVariation)}>
            <ListItemIcon>
              <AutoFixHighIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Try variation</ListItemText>
          </MenuItem>
        )}
        {onChangePosition && (
          <MenuItem onClick={action(onChangePosition)}>
            <ListItemIcon>
              <SwapVertIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change position</ListItemText>
          </MenuItem>
        )}
        {onRemove && (
          <MenuItem onClick={action(onRemove)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
